import { HttpHeaders, HttpClient } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { AgGridRequestService } from 'src/app/services/ag-grid-request.service';
import { BehaviorSubject, Subject } from "rxjs";
import {AddProductToSeasons,
  AddVendorItemToPoPayload,
  Breakdown,
  CreatePurchaseOrderPayload,
  EditPurchaseOrderPayload,
  PoDetail,
  SavePoPlanPayload,
  SaveSeasonPayload,
} from "src/app/models/purchasing.model";
import { Observable } from "rxjs";
import { GetPoListPayload } from "src/app/models/planyard.model";
import { map } from "rxjs/operators";
import { SaveProductEvent } from './../../models/purchasing.model';

@Injectable({
  providedIn: "root",
})
export class PurchasingService implements OnDestroy {
  constructor(private http: HttpClient, private agGridRequestService: AgGridRequestService) {}
  removeRow$: Subject<any> = new BehaviorSubject(null);
  private headers = new HttpHeaders({ "Content-Type": "application/json" });
  options = { headers: this.headers };
  private url = `api/purchasing`;
  selectedSidePanelSku: any;
  updatePurchasingQueueCount: Subject<boolean> = new Subject<boolean>();
  exportPoEvent:any[]=[];
 warehouseId:number;
  private destroy$ = new Subject<void>();

  SavePoPlan(payload: SavePoPlanPayload): Observable<number> {
    return this.http.post<number>(
      `/${this.url}/SavePoPlan`,
      payload,
      this.options
    );
  }

  GetPoPlan(planId) {
    return this.http.post(`/${this.url}/GetPoPlan`, { planId }, this.options);
  }

  GetPoDetails(poId: number): Observable<PoDetail> {
    return this.http.post<PoDetail>(
      `/${this.url}/GetPoDetails`,
      { poId },
      this.options
    );
  }

  SelectItemVendor(payload: { planId: number; VendorItemId: number }) {
    return this.http.post(
      `/${this.url}/SelectItemVendor`,
      { ...payload },
      this.options
    );
  }

  SelectItemPo(payload: {
    planId: number;
    itemVendorID: number;
    poId: number;
  }) {
    return this.http.post(
      `/${this.url}/SelectItemPo`,
      { ...payload },
      this.options
    );
  }

  ConvertToPo(planId) {
    return this.http.post(`/${this.url}/ConvertToPo`, { planId }, this.options);
  }

  GetPoList(
    data: GetPoListPayload,
    status: number,
    startRow: number,
    endRow: number
  ) {
    return this.http.post(
      `/${this.url}/GetPoList`,
      { ...data, status: status ? [status] : null, startRow, endRow },
      this.options
    );
  }

  EditPurchaseOrder(data: EditPurchaseOrderPayload) {
    return this.http.post(`/${this.url}/EditPurchaseOrder`, data, this.options);
  }

  GetPoStatuses() {
    return this.http.get(`/${this.url}/GetPoStatuses`, this.options);
  }

  CreatePurchaseOrder(payload: CreatePurchaseOrderPayload): Observable<number> {
    return this.http.post<number>(
      `/${this.url}/CreatePurchaseOrder`,
      payload,
      this.options
    );
  }

  AddVendorItemToPo(data: AddVendorItemToPoPayload) {
    return this.http.post(`/${this.url}/AddVendorItemToPo`, data, this.options);
  }

  DeletePO(PoId: number) {
    return this.http.post(`/${this.url}/DeletePO`, { PoId }, this.options);
  }

  ExportPoItems(poId: number) {
    return this.http.post(
      `/${this.url}/ExportPoItems`,
      { poId },
      {
        headers: this.headers,
        observe: "response",
        responseType: "blob",
      }
    );
  }

  GetPurchasingSummaries() {
    return this.http.post(
      `/${this.url}/GetPurchasingSummaries`,
      {},
      this.options
    );
  }

  GetSkusForPo(poId: number) {
    return this.http
      .post(`/${this.url}/GetSkusForPo`, { poId }, this.options)
      .pipe(
        map((res: any) => {
          return res.map((item) => {
            Object.keys(item).forEach((key) => {
              item[key.charAt(0).toUpperCase() + key.substring(1)] = item[key];
              delete item[key];
            });
            return item;
          });
        })
      );
  }

  MarkPoSkuShipped(poId: number, skuIds: number[]) {
    return this.http.post(
      `/${this.url}/MarkPoSkuShipped`,
      { poId, skuIds },
      this.options
    );
  }

  SaveSeason(payload: SaveSeasonPayload) {
    return this.http.post(`/${this.url}/SaveSeason`, payload, this.options);
  }

  DeleteSeason(seasonId: number) {
    return this.http.post(
      `/${this.url}/DeleteSeason`,
      { seasonId },
      this.options
    );
  }

  GetSeasons() {
    return this.http.post(`/${this.url}/GetSeasons`, {}, this.options);
  }

  GetSuggestionBreakDown(productId: number): Observable<Breakdown> {
    return this.http
      .post(`/${this.url}/GetSuggestionBreakDown`, { productId }, this.options)
      .pipe(
        map((e: any) => {
          e.demandSkus = e.demandSkus.map((demandSkus) => {
            return {
              Image: demandSkus.imageUrl,
              Title: demandSkus.title,
              Sku: demandSkus.sku,
              Asin: demandSkus.asin,
              // TODO: variable below should be updated across application
              Suggested: demandSkus.demandAmount,
              Qty: demandSkus.mapping,
            };
          });
          e.fbaStockSkus = e.fbaStockSkus.map((fbaStockSkus) => {
            return {
              Image: fbaStockSkus.imageUrl,
              Title: fbaStockSkus.title,
              Sku: fbaStockSkus.sku,
              Asin: fbaStockSkus.asin,
              TotalFbaStock: fbaStockSkus.totalFbaStock,
              AmountAdjustedOnItem: 0,
              SkuAdjustment: fbaStockSkus.skuAdjustment
            };
          });

          e.inboundPoTotal = this.CalculateTotals(e.inboundPos, 'qty');
          e.draftPoTotal = this.CalculateTotals(e.draftPos, 'qty');
          e.skuDemandTotal = this.CalculateTotalWithMapping(e.demandSkus, 'Suggested', 'Qty');
          e.skuFbaTotal = this.CalculateTotals(e.fbaStockSkus, 'SkuAdjustment');
          e.eventsTotal = this.CalculateTotals(e.events, 'amount');
          e.demandBufferTotal = this.CalculateTotals(e.demandBuffers, 'amount');
          e.seasonsTotal = this.CalculateTotals(e.seasons, 'amountChange');
          return e;
        })
      );
  }

  SaveProductEvent(payload: SaveProductEvent) {
    return this.http.post(`/${this.url}/SaveProductEvent`, payload, this.options);
  }

  DeleteProductEvent(productEventId: number) {
    return this.http.post(`/${this.url}/DeleteProductEvent`, {productEventId}, this.options);
  }

  AddProductToSeasons(payload: AddProductToSeasons) {
    return this.http.post(`/${this.url}/AddProductToSeasons`, payload, this.options);
  }

  CalculateTotals(array: any[], key: string): number {
    const total = array ? array.reduce((a, b) => {
      return a + b[key];
    }, 0) : 0;
    return total;
  }

  CalculateTotalWithMapping(array: any[], key: string, key2: string): number {
    const total = array ? array.reduce((a, b) => {
      return a + (b[key]*b[key2]);
    }, 0) : 0;
    return total;
  }

  GetSkuBreakDown(AccountSkuId: number): Observable<any> {
    return this.http.post(`/${this.url}/GetSkuBreakDown`, {AccountSkuId}, this.options);
  }

  PurchaseAll(filters, columnDefs, globalSearch?, vendorId?: number) {
    filters["Restock"] = {
      filter: 0,
      filterType: "number",
      type: "greaterThan"
    }
    const body = {
      filterModel: filters,
      vendorId: vendorId
    };

    const payload = this.agGridRequestService.transformAgGridRequest(
      body,
      columnDefs,
      globalSearch
    );

    return this.http.post(`/${this.url}/PurchaseAll`, {gridFilters: payload, loadAll: true, otherIds: []}, this.options);
  }

  recalculate(){
    return this.http.post(`/${this.url}/Recalculate`,{}, this.options);
  }

  GetVendorPoList(payload) {
    return this.http.post(
      `/${this.url}/GetPoList`,
      payload,
      this.options
    );
  }
  
 TypeAheadItemForLocation(payload: { qry: string; warehouseId: any }): Observable<any[]> {
   return this.http.post<any[]>(
  `/${this.url}/TypeaheadLocations`,payload,
      this.options
    );
  }


  savePoAttachment(payload){
    return this.http.post(`/${this.url}/SavePoAttachment`,payload, this.options);
  }

  deletePoAttachment(payload){
    return this.http.post(`/${this.url}/DeletePoAttachment`,payload, this.options);
  }
  exportPoList() {
    return this.http.get(`/${this.url}/ExportPoList`,  this.options);
  }

  ExportPoListHeaderDetail(isHeader=false) {
    return this.http.get(`/${this.url}/ExportPoListHeaderDetail?IsHeader=${isHeader}`,  this.options);

  }

  RemovePoPlanItem(payload) {
    return this.http.post(`/${this.url}/RemovePoPlanItem`, payload, this.options);
  }

  RemovePoOutStandingQty(poId){
    return this.http.post(`/${this.url}/RemovePoOutStandingQty`, poId, this.options);
  }

  MoveOutstandingQtyToPo(payload){
    return this.http.post(`/${this.url}/MoveOutstandingQtyToPo`, payload, this.options);
  }
  
  GetPoItemLocation(poId: number){
    return this.http.get(`/${this.url}/GetPoItemLocation?PoId=${poId} `, this.options);
  }

  GetPoItemLocationHistory(poId,itemId,locationId){
    return this.http.get(`/${this.url}/GetPoItemLocationHistory?PoId=${poId}&ItemId=${itemId}&LocationId=${locationId}`, this.options);
  }

  savePoItemToLocation(payload){
    return this.http.post(`/${this.url}/SavePoItemToLocation`, payload, this.options);
  }




  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }
  
    ExportPoPlan(planId){
    return this.http.post(`/${this.url}/ExportPoPlan`, {planId},{
      headers: this.headers,
      observe: "response",
      responseType: "blob",
    })}
  
  GetPurchasingQueueCount(){
    return this.http.get(
      `/${this.url}/GetPurchasingQueueCount`,
      this.options
    );
  }

  RemovePurchasingQueue(payload){
    return this.http.post(`/${this.url}/RemovePurchasingQueue`, payload, this.options);
  }

  createPurchasingQueue(payload){
    return this.http.post(`/${this.url}/CreatePurchasingQueue`, payload, this.options);
  }

  GetPurchasingQueue(){
    return this.http.post(`/${this.url}/GetPurchasingQueue`, this.options);

  }

  GetPoHistoryList(body, columnDefs, globalSearch?) {
    const payload = this.agGridRequestService.transformAgGridRequest(
      body,
      columnDefs,
      globalSearch
    );
    return this.http.post(
      `/${this.url}/GetPoHistoryList`,
      payload,
      this.options
    );
  }
}
